import { useEffect, useRef, useState } from 'react';
import { Palette } from './Palette';
import {
  useConversation,
  type ConversationItem,
} from '@/services/ConversationService';
import { Column, Font, PrimaryButton } from './DesignSystem';

function generatePreviewConversationID(): string {
  return `preview-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const ValidConversationItemTypes = new Set([
  'defineColor',
  'removeColor',
  'updateColorPalette',
  'updateTextStyles',
  'message',
  'defineTextStyle',
  'removeTextStyle',
  'addScreen',
  'removeScreen',
  'writeProperties',
  'defineComponentStyle',
  'removeComponentStyle',
  'declareFunctionComponent',
  'removeFunctionComponent',
  'addChildComponent',
  'createComponent',
  'describeScreen',
  'connect',
  'createVersion',
]);

type ValidationResult =
  | { valid: true; items: ConversationItem[] }
  | { valid: false; error: string };

function validateConversationItems(parsed: unknown): ValidationResult {
  if (!Array.isArray(parsed)) {
    return { valid: false, error: 'JSON must be an array' };
  }

  for (let i = 0; i < parsed.length; i++) {
    const item = parsed[i];
    if (typeof item !== 'object' || item === null) {
      return { valid: false, error: `Item at index ${i} is not an object` };
    }

    if (!('type' in item) || typeof item.type !== 'string') {
      return {
        valid: false,
        error: `Item at index ${i} is missing 'type' field`,
      };
    }

    if (!ValidConversationItemTypes.has(item.type)) {
      return {
        valid: false,
        error: `Item at index ${i} has unknown type '${item.type}'`,
      };
    }
  }

  return { valid: true, items: parsed as ConversationItem[] };
}

export function JSONImportPalette() {
  const [importedJSONText, _setImportedJSONText] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { state: conversationState, dispatch: conversationDispatch } =
    useConversation();
  const hasImportedRef = useRef<boolean>(false);

  function setImportedJSONText(importedJSONText: string) {
    hasImportedRef.current = true;
    setError(null);
    _setImportedJSONText(importedJSONText);
  }

  useEffect(() => {
    if (hasImportedRef.current) {
      return;
    }

    if (conversationState.type !== 'conversation') {
      return;
    }

    _setImportedJSONText(JSON.stringify(conversationState.items));
  }, [conversationState, _setImportedJSONText]);

  const isLoading = conversationState.loading !== undefined;
  const canImport =
    hasImportedRef.current && importedJSONText !== '' && !isLoading;

  function handleImport() {
    setError(null);

    let parsed: unknown;
    try {
      parsed = JSON.parse(importedJSONText);
    } catch {
      setError('Invalid JSON syntax');
      return;
    }

    const validation = validateConversationItems(parsed);
    if (!validation.valid) {
      setError(validation.error);
      return;
    }

    const conversationID =
      conversationState.type === 'conversation'
        ? conversationState.conversationID
        : generatePreviewConversationID();

    conversationDispatch({ type: 'reset-conversation' });
    conversationDispatch({
      type: 'hydrate-conversation',
      conversationID,
      items: validation.items,
    });
  }

  return (
    <Palette title="JSON import" windowID="json-import">
      <Column style={{ gap: 16 }}>
        <textarea
          style={{
            height: 120,
            padding: 16,
            borderColor: error ? '#dc2626' : undefined,
          }}
          value={importedJSONText}
          onChange={(e) => {
            setImportedJSONText(e.target.value);
          }}
          placeholder="Paste conversation items JSON here..."
        />
        {error && (
          <Font kind="font-subtitle">
            <span style={{ color: '#dc2626' }}>{error}</span>
          </Font>
        )}
        <PrimaryButton
          text="Import"
          onClick={handleImport}
          isDisabled={!canImport}
        />
      </Column>
    </Palette>
  );
}
